// Compose an "annotated review" PDF: header with job/iteration info, then
// the design image with numbered pins overlaid, then a tidy list of pins and
// unpinned comments grouped underneath. Replaces the old PNG-only export.
//
// Layout (A4 portrait, 40 pt margins):
//   ┌─────────────────────────────────────────┐
//   │  GC-XXX · Project Name                   │  <- header block
//   │  Customer · Project type                 │
//   │  Iteration V.02 · Ready for review · date│
//   ├─────────────────────────────────────────┤
//   │             [design image]               │  <- image with numbered pin
//   │      1        2                          │     markers baked in
//   │                                          │
//   ├─────────────────────────────────────────┤
//   │  Pin 1  Jane Doe · CUSTOMER   2026-07-30 │  <- comment list, wrapped
//   │         Move logo higher                 │
//   │  Pin 2  ...                              │
//   │  •      unpinned comments last           │
//   └─────────────────────────────────────────┘

import jsPDF from "jspdf";

const NAVY = "#0E1B4D";
const ORANGE = "#F26522";
const MUTED = "#6B7280";
const HAIRLINE = "#E5E7EB";

const PAGE = { w: 595.28, h: 841.89, margin: 40 };

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Composite the design image + numbered pin markers to a data URL so the
 * whole thing can be embedded in the PDF as one image (keeps pin marks aligned
 * to pixels, works for arbitrarily large source images).
 */
function bakePinsIntoImage(img, pinnedComments) {
  const canvas = document.createElement("canvas");
  const maxW = 1600; // cap to keep PDF weight sane
  const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  pinnedComments.forEach((c, i) => {
    const cx = c.pin_x * canvas.width;
    const cy = c.pin_y * canvas.height;
    const r = Math.max(16, Math.min(24, canvas.width * 0.015));
    // Shadow
    ctx.beginPath(); ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.fill();
    // Orange fill
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = ORANGE; ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "#ffffff"; ctx.stroke();
    // Number
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${Math.round(r * 0.9)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), cx, cy);
  });

  return canvas.toDataURL("image/jpeg", 0.9);
}

function fmtDate(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleDateString(); } catch { return ""; }
}
function fmtDateTime(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return ""; }
}

/**
 * Render the annotated-review PDF and return a Blob.
 *
 * @param {object} opts
 * @param {string} opts.imageUrl        - blob URL of the source design image
 * @param {Array}  opts.comments        - full comment list for this file
 * @param {object} opts.job             - job object (job_number, project_name, project_type, customer{...}, phase)
 * @param {object} opts.iteration       - the design iteration (iteration_number, status, uploaded_at)
 * @param {object} opts.file            - the design file (design_number)
 * @returns {Promise<Blob>} the PDF Blob
 */
export async function renderAnnotatedDesignPdf({ imageUrl, comments, job, iteration, file }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const { w: PW, h: PH, margin: M } = PAGE;
  const contentW = PW - M * 2;

  const pinnedComments = comments.filter((c) => c.pin_x != null && c.pin_y != null);
  const unpinnedComments = comments.filter((c) => c.pin_x == null || c.pin_y == null);

  // ---- Header block ----
  let y = M;
  const jobNum = job?.job_number || "—";
  const projectName = job?.project_name || "Untitled project";
  const customerName = job?.customer
    ? [job.customer.first_name, job.customer.last_name].filter(Boolean).join(" ")
    : "";
  const business = job?.customer?.business_name || "";
  const projectType = job?.project_type || "";
  const iterLabel = iteration?.iteration_number != null
    ? `V.${String(iteration.iteration_number).padStart(2, "0")}`
    : (file?.design_number || "Design");
  const iterStatus = (iteration?.status || "").replace(/_/g, " ") || "";
  const uploadedAt = fmtDate(iteration?.uploaded_at);
  const generatedAt = fmtDateTime(new Date().toISOString());

  // Chip: job number
  doc.setFillColor(NAVY);
  const chipText = `${jobNum}`;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const chipW = doc.getTextWidth(chipText) + 14;
  doc.roundedRect(M, y, chipW, 16, 2, 2, "F");
  doc.setTextColor("#ffffff");
  doc.text(chipText, M + 7, y + 11);

  // Generated timestamp (right)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text(`Generated ${generatedAt}`, PW - M, y + 11, { align: "right" });

  y += 26;

  // Project name (title)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(NAVY);
  const titleLines = doc.splitTextToSize(projectName, contentW);
  doc.text(titleLines, M, y + 16);
  y += titleLines.length * 22 + 4;

  // Customer + type row
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor("#374151");
  const custBits = [customerName, business, projectType].filter(Boolean).join("  ·  ");
  if (custBits) {
    doc.text(custBits, M, y + 10);
    y += 16;
  }

  // Iteration line
  doc.setFontSize(10);
  doc.setTextColor(ORANGE);
  doc.setFont("helvetica", "bold");
  const iterBits = [
    `Iteration ${iterLabel}`,
    iterStatus,
    uploadedAt ? `Uploaded ${uploadedAt}` : "",
    `${pinnedComments.length} pin${pinnedComments.length === 1 ? "" : "s"}`,
    `${unpinnedComments.length} note${unpinnedComments.length === 1 ? "" : "s"}`,
  ].filter(Boolean).join("  ·  ");
  doc.text(iterBits, M, y + 10);
  y += 18;

  // Hairline
  doc.setDrawColor(HAIRLINE);
  doc.setLineWidth(0.5);
  doc.line(M, y, PW - M, y);
  y += 14;

  // ---- Image ----
  const img = await loadImage(imageUrl);
  const bakedDataUrl = bakePinsIntoImage(img, pinnedComments);

  // Fit image into remaining width, keep aspect. Cap height so comments fit.
  const imgAspect = img.naturalHeight / img.naturalWidth;
  const drawW = contentW;
  const drawH = drawW * imgAspect;
  // If the image is very tall, cap it and let it use up to ~55% of the page.
  const maxImgH = PH * 0.55;
  const finalH = Math.min(drawH, maxImgH);
  const finalW = finalH === drawH ? drawW : finalH / imgAspect;
  const imgX = M + (contentW - finalW) / 2;

  doc.addImage(bakedDataUrl, "JPEG", imgX, y, finalW, finalH, undefined, "FAST");
  y += finalH + 16;

  // ---- Comment list ----
  const ensureSpace = (needed) => {
    if (y + needed > PH - M) {
      doc.addPage();
      y = M;
    }
  };

  // Section header
  ensureSpace(30);
  doc.setDrawColor(HAIRLINE);
  doc.line(M, y, PW - M, y);
  y += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(NAVY);
  doc.text(`Comments (${comments.length})`, M, y);
  y += 14;

  if (comments.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(MUTED);
    doc.text("No comments on this design.", M, y);
  } else {
    const drawEntry = (badgeText, badgePinned, headerText, bodyText) => {
      // Wrap body once so we can estimate block height
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      const bodyLines = doc.splitTextToSize(bodyText || "(no text)", contentW - 40);
      const blockH = 14 + bodyLines.length * 13 + 10;
      ensureSpace(blockH);

      // Badge (orange circle for pinned, gray for unpinned)
      const badgeCX = M + 12;
      const badgeCY = y + 4;
      doc.setFillColor(badgePinned ? ORANGE : "#D1D5DB");
      doc.circle(badgeCX, badgeCY, 10, "F");
      doc.setTextColor("#ffffff");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(String(badgeText), badgeCX, badgeCY + 3, { align: "center" });

      // Header line (author · role · date)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(NAVY);
      doc.text(headerText, M + 32, y + 6);

      // Body lines
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor("#111827");
      let by = y + 20;
      bodyLines.forEach((ln) => {
        doc.text(ln, M + 32, by);
        by += 13;
      });
      y = by + 4;

      // Faint separator
      doc.setDrawColor(HAIRLINE);
      doc.setLineWidth(0.3);
      doc.line(M + 32, y, PW - M, y);
      y += 6;
    };

    // Pinned first — numbered to match image markers
    pinnedComments.forEach((c, i) => {
      const author = c.author_name || "Reviewer";
      const role = (c.author_role || "").toUpperCase();
      const dt = fmtDateTime(c.created_at);
      const header = [author, role, dt].filter(Boolean).join("  ·  ");
      drawEntry(i + 1, true, header, c.text);
    });

    // Unpinned last
    if (unpinnedComments.length > 0) {
      ensureSpace(24);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(MUTED);
      doc.text(`General notes (${unpinnedComments.length})`, M, y + 6);
      y += 16;
      unpinnedComments.forEach((c) => {
        const author = c.author_name || "Reviewer";
        const role = (c.author_role || "").toUpperCase();
        const dt = fmtDateTime(c.created_at);
        const header = [author, role, dt].filter(Boolean).join("  ·  ");
        drawEntry("•", false, header, c.text);
      });
    }
  }

  // Footer on every page
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(MUTED);
    doc.text(`${projectName} · ${iterLabel}`, M, PH - 20);
    doc.text(`Page ${p} of ${total}`, PW - M, PH - 20, { align: "right" });
  }

  return doc.output("blob");
}
